import { db } from "@/lib/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp,
} from "firebase/firestore";
import { Product } from "@/types/products";
import { convertDoc, uploadImage } from "./utils";
import { updateCategoryProductCount } from "./categories";

const PRODUCTS_COLLECTION = "products";

// Get all products
export async function getProducts(): Promise<Product[]> {
  try {
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const querySnapshot = await getDocs(productsRef);
    
    return querySnapshot.docs.map(doc => convertDoc<Product>(doc));
  } catch (error) {
    console.error("Error fetching products:", error);
    throw new Error("Failed to fetch products");
  }
}

// Get a single product by ID
export async function getProduct(productId: string): Promise<Product | null> {
  try {
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    const docSnapshot = await getDoc(productRef);
    
    if (!docSnapshot.exists()) {
      return null;
    }
    
    return convertDoc<Product>(docSnapshot);
  } catch (error) {
    console.error("Error fetching product:", error);
    throw new Error("Failed to fetch product");
  }
}

// Alias for getProduct to maintain compatibility with reviews.ts
export const getProductById = getProduct;

// Get products by category
export async function getProductsByCategory(category: string): Promise<Product[]> {
  try {
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const q = query(productsRef, where("category", "==", category));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => convertDoc<Product>(doc));
  } catch (error) {
    console.error("Error fetching products by category:", error);
    throw new Error("Failed to fetch products by category");
  }
}

// Add a new product
export async function addProduct(productData: any): Promise<Product> {
  try {
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const docRef = await addDoc(productsRef, {
      ...productData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // Update the category count after adding a product
    if (productData.category) {
      await updateCategoryProductCount(productData.category);
    }
    
    return {
      id: docRef.id,
      ...productData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  } catch (error) {
    console.error("Error adding product:", error);
    throw new Error("Failed to add product");
  }
}

// Update a product
export async function updateProduct(
  productId: string, 
  productData: Partial<Omit<Product, 'id' | 'createdAt' | 'updatedAt'>> & { updatedAt?: any }
): Promise<void> {
  try {
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    
    // Get the product before updating to check if the category changed
    const oldProductDoc = await getDoc(productRef);
    const oldCategory = oldProductDoc.exists() ? oldProductDoc.data().category : null;
    
    await updateDoc(productRef, {
      ...productData,
      updatedAt: serverTimestamp(),
    });
    
    // Update the category counts if category changed
    const newCategory = productData.category;
    if (newCategory && oldCategory !== newCategory) {
      // Update both the old and new category counts
      if (oldCategory) {
        await updateCategoryProductCount(oldCategory);
      }
      await updateCategoryProductCount(newCategory);
    } else if (newCategory) {
      // If category didn't change, still update the count to ensure consistency
      await updateCategoryProductCount(newCategory);
    }
  } catch (error) {
    console.error("Error updating product:", error);
    throw new Error("Failed to update product");
  }
}

// Delete a product
export async function deleteProduct(productId: string): Promise<void> {
  try {
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    
    // Get the product to check its category
    const productDoc = await getDoc(productRef);
    if (!productDoc.exists()) {
      throw new Error("Product not found");
    }
    
    const category = productDoc.data().category;
    
    // Delete the product
    await deleteDoc(productRef);
    
    // Update the category count after deleting a product
    if (category) {
      await updateCategoryProductCount(category);
    }
  } catch (error) {
    console.error("Error deleting product:", error);
    throw new Error("Failed to delete product");
  }
}

// Upload product image
export async function uploadProductImage(file: File): Promise<string> {
  const path = `products/${Date.now()}_${file.name}`;
  return uploadImage(file, path);
}

// Update product rating based on reviews
export async function updateProductRating(productId: string): Promise<void> {
  try {
    // This function is called from reviews.ts to update a product's rating
    // when reviews are added, updated, approved, or deleted
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    
    // Check if product exists
    const productDoc = await getDoc(productRef);
    if (!productDoc.exists()) {
      throw new Error("Product not found");
    }
    
    // Calculate new average rating from reviews
    // This would typically call a function from reviews.ts to get the average
    // For now, we'll update with a placeholder calculation to avoid circular dependencies
    const reviewsRef = collection(db, "reviews");
    const q = query(
      reviewsRef,
      where("productId", "==", productId),
      where("isApproved", "==", true)
    );
    
    const querySnapshot = await getDocs(q);
    
    let averageRating = 0;
    if (!querySnapshot.empty) {
      let totalRating = 0;
      querySnapshot.docs.forEach(doc => {
        const review = doc.data();
        totalRating += review.rating;
      });
      
      averageRating = totalRating / querySnapshot.size;
    }
    
    // Update the product with the new rating
    await updateDoc(productRef, {
      rating: averageRating,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error updating product rating:", error);
    throw new Error("Failed to update product rating");
  }
}

// Update product stock
export async function updateProductStock(productId: string, quantity: number, isRefund: boolean = false): Promise<boolean> {
  try {
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      console.error(`Product ${productId} not found`);
      return false;
    }

    const product = productDoc.data();
    const currentStock = product.stock || 0;
    let newStock = currentStock;

    if (isRefund) {
      // Adding stock back (refund/cancel)
      newStock = currentStock + quantity;
    } else {
      // Removing stock (purchase)
      newStock = currentStock - quantity;
      
      // Prevent negative stock
      if (newStock < 0) {
        console.error(`Cannot update stock: Insufficient stock for product ${productId}. Required: ${quantity}, Available: ${currentStock}`);
        return false;
      }
    }

    await updateDoc(productRef, {
      stock: newStock,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error("Error updating product stock:", error);
    throw new Error("Failed to update product stock");
  }
}
